/* Blush PDP behaviour: gallery, variant selection, accordions.
   Deliberately independent of Dawn's product-info.js — this template renders a
   single custom section, so there is no section-rendering round trip to make. */

if (!customElements.get('blush-gallery')) {
  customElements.define(
    'blush-gallery',
    class BlushGallery extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-gallery-main]');
        this.mainImage = this.main && this.main.querySelector('img');
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        if (!this.mainImage || !this.thumbs.length) return;

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.select(thumb));
        });
      }

      select(thumb) {
        if (thumb.getAttribute('aria-current') === 'true') return;

        const src = thumb.dataset.fullSrc;
        const srcset = thumb.dataset.fullSrcset || '';
        const alt = thumb.dataset.fullAlt || '';
        if (!src) return;

        this.thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));

        // 200ms cross-fade, as suggested in the handoff.
        this.main.classList.add('is-swapping');
        const swap = () => {
          this.mainImage.src = src;
          this.mainImage.srcset = srcset;
          this.mainImage.alt = alt;
          this.main.classList.remove('is-swapping');
        };

        if (this.mainImage.decode) {
          const preload = new Image();
          preload.src = src;
          preload.decode().then(swap).catch(swap);
        } else {
          setTimeout(swap, 200);
        }
      }
    }
  );
}

if (!customElements.get('blush-variant-picker')) {
  customElements.define(
    'blush-variant-picker',
    class BlushVariantPicker extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-variant-data]');
        if (!data) return;

        try {
          this.variants = JSON.parse(data.textContent);
        } catch (e) {
          return;
        }

        this.form = document.getElementById(this.dataset.formId);
        this.idInput = this.form && this.form.querySelector('input[name="id"]');
        this.priceEl = document.getElementById(this.dataset.priceId);
        this.submit = document.getElementById(this.dataset.submitId);

        this.addEventListener('change', () => this.onChange());
        this.refreshAvailability();
      }

      get selectedValues() {
        return Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((input) => input.value);
      }

      get optionGroupCount() {
        return this.querySelectorAll('[data-option-index]').length;
      }

      findVariant(values) {
        return this.variants.find((variant) =>
          variant.options.every((option, index) => option === values[index])
        );
      }

      onChange() {
        // Every option must be chosen before a variant exists. The design ships
        // with nothing preselected, so this is the normal first state.
        const complete = this.selectedValues.length === this.optionGroupCount;
        const variant = complete ? this.findVariant(this.selectedValues) : null;
        this.current = variant;

        this.updateIdInput(variant);
        this.updatePrice(variant);
        this.updateSubmit(variant, complete);
        this.updateUrl(variant);
        this.refreshAvailability();
      }

      updateIdInput(variant) {
        if (!this.idInput) return;
        this.idInput.value = variant ? variant.id : '';
        this.idInput.disabled = !variant || !variant.available;
      }

      updatePrice(variant) {
        if (!this.priceEl || !variant) return;
        const amount = this.priceEl.querySelector('[data-price-amount]');
        const was = this.priceEl.querySelector('[data-price-was]');
        if (amount) amount.innerHTML = variant.price;
        if (was) {
          if (variant.compare_at_price) {
            was.innerHTML = variant.compare_at_price;
            was.hidden = false;
          } else {
            was.hidden = true;
          }
        }
      }

      updateSubmit(variant, complete) {
        if (!this.submit) return;
        const text = this.submit.querySelector('.blush-atc__text');
        const strings = window.variantStrings || {};

        if (!complete) {
          this.submit.disabled = true;
          if (text) text.textContent = this.dataset.selectPrompt || 'Select a size';
        } else if (!variant) {
          this.submit.disabled = true;
          if (text) text.textContent = strings.unavailable || 'Unavailable';
        } else if (!variant.available) {
          this.submit.disabled = true;
          if (text) text.textContent = strings.soldOut || 'Sold out';
        } else {
          this.submit.disabled = false;
          if (text) text.textContent = strings.addToCart || 'Add to cart';
        }
      }

      updateUrl(variant) {
        if (!variant || this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
      }

      /* Mark option values that cannot form an available variant with the
         current selection. Only meaningful on multi-option products. */
      refreshAvailability() {
        const groups = Array.from(this.querySelectorAll('[data-option-index]'));
        if (groups.length < 2) {
          this.querySelectorAll('input[type="radio"]').forEach((input) => {
            const values = this.selectedValues.slice();
            values[0] = input.value;
            const variant = this.findVariant(values);
            input.classList.toggle('is-unavailable', !variant || !variant.available);
          });
          return;
        }

        const selected = this.selectedValues;
        groups.forEach((group) => {
          const index = Number(group.dataset.optionIndex);
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            const candidate = selected.slice();
            candidate[index] = input.value;
            const match = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options.every((option, i) => (i === index ? option === input.value : option === candidate[i]))
            );
            input.classList.toggle('is-unavailable', !match);
          });
        });
      }
    }
  );
}

if (!customElements.get('blush-accordion')) {
  customElements.define(
    'blush-accordion',
    class BlushAccordion extends HTMLElement {
      connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('[data-accordion-button]'));

        this.buttons.forEach((button) => {
          button.addEventListener('click', () => {
            const isOpen = button.getAttribute('aria-expanded') === 'true';
            // One open at a time; clicking an open panel closes it.
            this.buttons.forEach((other) => this.setState(other, false));
            if (!isOpen) this.setState(button, true);
          });
        });
      }

      setState(button, open) {
        button.setAttribute('aria-expanded', String(open));
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;

        const inner = panel.firstElementChild;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!inner || reduceMotion) {
          panel.classList.toggle('is-open', open);
          panel.style.height = '';
          return;
        }

        // Animate between explicit pixel heights, then hand back to CSS so an
        // open panel can reflow naturally (height: auto cannot be transitioned).
        const from = panel.getBoundingClientRect().height;
        const to = open ? inner.getBoundingClientRect().height : 0;

        if (from === to) {
          panel.classList.toggle('is-open', open);
          panel.style.height = '';
          return;
        }

        panel.style.height = `${from}px`;
        panel.classList.toggle('is-open', open);
        void panel.offsetHeight;
        panel.style.height = `${to}px`;

        const onEnd = (event) => {
          if (event.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', onEnd);
          panel.style.height = '';
        };
        panel.addEventListener('transitionend', onEnd);
      }
    }
  );
}

if (!customElements.get('blush-review-list')) {
  customElements.define(
    'blush-review-list',
    class BlushReviewList extends HTMLElement {
      connectedCallback() {
        this.perPage = parseInt(this.dataset.perPage, 10) || 4;
        // Only review cards are paginated. Group headings are revealed based on
        // what is visible, so "reviews per page" counts actual reviews.
        this.items = Array.from(this.querySelectorAll('[data-review]'));
        this.groups = Array.from(this.querySelectorAll('[data-review-group]'));
        if (!this.items.length) return;

        this.prevButton = this.querySelector('[data-review-prev]');
        this.nextButton = this.querySelector('[data-review-next]');
        this.status = this.querySelector('[data-review-status]');
        this.pager = this.querySelector('.blush-reviews__pager');

        this.page = 0;
        this.pageCount = Math.ceil(this.items.length / this.perPage);

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.goTo(this.page - 1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.goTo(this.page + 1));

        // A single page needs no controls at all.
        if (this.pageCount < 2 && this.pager) this.pager.hidden = true;

        this.render();
      }

      goTo(page) {
        const target = Math.min(Math.max(page, 0), this.pageCount - 1);
        if (target === this.page) return;
        this.page = target;
        this.render();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      render() {
        const start = this.page * this.perPage;
        const end = start + this.perPage;
        this.items.forEach((item, index) => {
          item.hidden = index < start || index >= end;
        });

        this.groups.forEach((heading) => {
          const key = heading.dataset.reviewGroup;
          heading.hidden = !this.items.some((item) => !item.hidden && item.dataset.group === key);
        });

        if (this.prevButton) this.prevButton.disabled = this.page === 0;
        if (this.nextButton) this.nextButton.disabled = this.page >= this.pageCount - 1;
        if (this.status) this.status.textContent = `Page ${this.page + 1} of ${this.pageCount}`;
      }
    }
  );
}
